import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';

import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { TestExecutionDTO, TestExecutionFilterDTO, TestScenarioDTO, TestScenarioFilterDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField } from '../../../shared/components';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { TestComponentDTO } from '../../../shared/interfaces/testingSphere/TestComponentDTO';
import { TestComponentFilterDTO } from '../../../shared/interfaces/testingSphere/TestComponentFilterDTO';
import { TestExecutionView } from './TestExecutionView';
import { TestLog } from '../test-log/TestLog';

interface ITestExecutionProps {
    data: any;
    testScenarioId?: string;
}

export const TestExecution: React.FC<ITestExecutionProps> = ({ data, testScenarioId }) => {

    const [testScenarioList, setTestScenarioList] = useState<TestScenarioDTO[]>([]);
    const [testComponentList, setTestComponentList] = useState<TestComponentDTO[]>([]);
    const [testExecutionList, setTestExecutionList] = useState<TestExecutionDTO[]>([]);
    const [testExecutionFilterDTO, setTestExecutionFilterDTO] = useState<TestExecutionFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    useEffect(() => {
        setTestExecutionFilterDTO({
            ...testExecutionFilterDTO,
            testComponentId: testScenarioId ? '' : data.id ?? '',
            testScenarioId: testScenarioId ? data.id : ''
        } as TestExecutionFilterDTO);

        handleTestScenarioGetAllAsync();
        handleTestComponentGetAllAsync();
    }, []);

    useEffect(() => {
        handleTestExecutionGetAllViewAsync();
    }, [testExecutionFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleTestExecutionGetAllViewAsync();
    }, [actionExecuted]);

    useEffect(() => {
        handleTestComponentGetAllAsync();
    }, [testExecutionFilterDTO?.testScenarioId]);

    const handleTestScenarioGetAllAsync = useCallback(async () => {
        const testScenarioFilterDTO: TestScenarioFilterDTO = {
            name: '',
            testPackageId: '',
        };
        GenericService.GetAllAsync1('testingsphere.testscenario', testScenarioFilterDTO, setLoading, setTestScenarioList, showMessage);
    }, []);

    const handleTestComponentGetAllAsync = useCallback(async () => {
        const testComponentFilterDTO: TestComponentFilterDTO = {
            name: '',
            testScenarioId: testExecutionFilterDTO?.testScenarioId,
        };
        GenericService.GetAllAsync1('testingsphere.testcomponent', testComponentFilterDTO, setLoading, setTestComponentList, showMessage);
    }, []);

    const handleTestExecutionGetAllViewAsync = useCallback(async () => {
        if (testExecutionFilterDTO === undefined)
            return;

        GenericService.GetAllViewAsync1('testingsphere.testExecution', testExecutionFilterDTO, setActionExecuted, setLoading, setTestExecutionList, showMessage);
    }, [testExecutionFilterDTO]);

    const handleClearFilterClick = () => {
        setTestExecutionFilterDTO({ ...testExecutionFilterDTO, name: '', testComponentId: testScenarioId ? '' : data.id ?? '' } as TestExecutionFilterDTO);
    };

    function handleSearchClick() {
        handleTestExecutionGetAllViewAsync();
    }

    const handleViewDetatils = (data: string) => {
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        setDeleteRecord(data);
        setEditRecord('');
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        // { id: 'new', text: 'New Feature', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: testExecutionList.length },
        { label: 'Name', value: testExecutionFilterDTO?.name },
        { label: 'TestScenario', value: !data ? testScenarioList.find(x => x.id === testExecutionFilterDTO?.testScenarioId)?.name : undefined },
        { label: 'TestComponent', value: !data ? testComponentList.find(x => x.id === testExecutionFilterDTO?.testComponentId)?.name : undefined },
    ];

    const columns: ICardColumns[] = [
        { field: 'testComponent', label: 'Test Component', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'executionStatus', label: 'Execution Status', size: 1, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'startExecutionDateTime', label: 'Start Execution', size: 2, type: 'dateTime', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'endExecutionDateTime', label: 'End Execution', size: 2, type: 'dateTime', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'elapseTime', label: 'ElapseTime', size: 1, type: 'text', visible: true, isChip: false, hasFieldLine: false },
        { field: 'testAgent', label: 'Test Agent', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'cycle', label: 'Cycle', size: 1, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='APPLICATION'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                value={testExecutionFilterDTO?.name}
                                setValue={setTestExecutionFilterDTO}
                                obj={testExecutionFilterDTO}
                                placeholder={'Name'}
                                maxLength={200}
                            />
                        </Grid>
                        {!data && (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <SelectField
                                        id='testScenarioId'
                                        label='TestScenario Name'
                                        value={testExecutionFilterDTO?.testScenarioId}
                                        setValue={setTestExecutionFilterDTO}
                                        obj={testExecutionFilterDTO}
                                        valueList={testScenarioList}
                                        disabled={testScenarioId ? true : false}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <SelectField
                                        id='testComponentId'
                                        label='TestComponent Name'
                                        value={testExecutionFilterDTO?.testComponentId}
                                        setValue={setTestExecutionFilterDTO}
                                        obj={testExecutionFilterDTO}
                                        valueList={testComponentList}
                                        hasNoneSelect={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        )}
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <CardList
                    columns={columns}
                    rows={testExecutionList}
                    actions={cardActions}
                    hasChildren={true}
                    setRowExpanded={setExpandedData}
                    cardSize={12}
                    childrenTitle={'TESTLOG'}
                    childrenComponent={(
                        <Box width={'100%'}>
                            <TestLog data={expadedData} />
                        </Box>
                    )}
                />
                {editRecord && (
                    <TestExecutionView
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'testingsphere.testexecution'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />)}
            </LayoutDashboard>
        </React.Fragment >
    );
};